"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Mail, Loader } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
function NewsletterFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-md border border-border bg-card p-6 text-center sm:p-8">
        <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-full bg-muted text-foreground">
          <CheckCircle2 className="size-5" />
        </div>
        <h4 className="font-default text-lg font-semibold text-foreground">
          Subscription confirmed
        </h4>
        <p className="mt-1.5 text-sm text-muted-foreground">
          {successMessage || "You will receive the next briefing this Thursday morning."}
        </p>
        <Button onClick={resetForm} variant="outline" size="sm" className="mt-5 rounded-sm border-border text-foreground hover:bg-muted">
          Subscribe another address
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex-1">
          <FormField control={control} name="email" rules={{
          required: "Business email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid work email address"
          }
        }} render={({
          field
        }) => <FormItem className="space-y-1.5">
                <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Business Email
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input type="email" placeholder="name@company.com" className="h-11 rounded-sm border-input bg-card pl-10 pr-4 text-sm text-card-foreground shadow-2xs placeholder:text-muted-foreground/70 focus-visible:border-ring focus-visible:ring-ring" {...field} />
                  </div>
                </FormControl>
                <FormMessage className="text-xs text-destructive" />
              </FormItem>} />
        </div>
        <div className="sm:pt-6">
          <Button type="submit" disabled={isSubmitting} className="h-11 w-full rounded-sm bg-primary px-6 font-medium text-primary-foreground shadow-sm transition-[background-color,box-shadow] hover:bg-primary/90 sm:w-auto">
            {isSubmitting ? <>
                <Loader className="mr-2 size-4 animate-spin" />
                <span>Processing...</span>
              </> : <>
                <span>Subscribe</span>
                <ArrowRight className="size-4" />
              </>}
          </Button>
        </div>
      </div>

      {submitError && <div className="rounded-sm border border-destructive/20 bg-destructive/10 p-3 text-xs text-destructive">
          {submitError}
        </div>}
    </Form>;
}
export default function Briefing() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="newsletter-cta" data-nav="light" className="relative overflow-hidden bg-background py-16 sm:py-20 lg:py-24">
      <div className="container mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative mx-auto max-w-[840px] rounded-md border border-border bg-card p-8 shadow-md sm:p-12 lg:p-14">
          {/* Masthead rule decorative top bar */}
          <div className="mb-8 border-b border-border pb-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Editorial Dispatch
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                Weekly Edition
              </span>
            </div>
          </div>

          <div className="text-center sm:text-left">
            <h2 className="font-serif text-2xl font-semibold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
              Get the weekly executive briefing
            </h2>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">
              Curated research summaries, benchmark datasets, and strategic analysis delivered to your inbox every Thursday.
            </p>
          </div>

          <div className="mt-8">
            <FormProvider formId="home_newsletter-cta_form" sectionName="newsletter-cta" defaultValues={{
            email: ""
          }} validationRules={{
            email: {
              required: true,
              pattern: "email"
            }
          }} submitText="Subscribe" successMessage="Thank you for subscribing to MinfoMedia. Check your inbox for confirmation." errorMessage="Unable to process subscription. Please check your email and try again.">
              <NewsletterFormInner />
            </FormProvider>
          </div>

          <div className="mt-6 flex flex-col items-center justify-between gap-3 border-t border-border pt-5 text-xs text-muted-foreground sm:flex-row">
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="size-4 text-muted-foreground" />
              <span>No promotional sponsor emails. Unsubscribe at any time.</span>
            </div>
            <div>
              <span>Protected under our </span>
              <Link to="/PrivacyDataProtectionPage" className="underline underline-offset-4 hover:text-foreground">
                Privacy policy
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}